import { useEffect, useRef } from 'react';
import { focusRing } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Lightweight contenteditable wrapper. Single-line by default; pass
// `multiline` for paragraph fields. Emits onChange with the plain text
// content (no rich-text HTML at MVP — Phase 3B-3b ships plain only).
//
// Why a hand-rolled contenteditable instead of an <input>/<textarea>:
// the editor renders blocks at near-final visual fidelity. A site's
// headline at text-4xl needs to BE that <h1> visually so the user
// sees what they're shipping; an <input> with text-4xl breaks the
// illusion. Inline contenteditable keeps the document feel.

interface EditableTextProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  'aria-label'?: string;
}

export function EditableText({
  value,
  onChange,
  placeholder,
  multiline = false,
  className,
  'aria-label': ariaLabel,
}: EditableTextProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Sync `value` prop → DOM textContent without disturbing the caret
  // when the change came from this same field.
  useEffect(() => {
    if (!ref.current) return;
    if (ref.current.textContent !== value) {
      ref.current.textContent = value;
    }
  }, [value]);

  function handleInput(e: React.FormEvent<HTMLDivElement>) {
    const text = e.currentTarget.textContent ?? '';
    onChange(text);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      ref.current?.blur();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    // Force plain-text paste — no rich formatting smuggled in.
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }

  const isEmpty = value.length === 0;

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline={multiline}
      aria-label={ariaLabel ?? placeholder}
      data-placeholder={placeholder}
      data-empty={isEmpty ? 'true' : undefined}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      spellCheck
      className={cn(
        'outline-none transition-colors',
        // Real focus ring — the faint bg tint alone is well under the 3:1 a
        // focus indicator needs (WCAG 2.4.7 / 1.4.11). Ring + tint together.
        // The ring itself is now the SHARED one every text input uses (T14):
        // this element is a contentEditable on the builder canvas, so it takes
        // none of inputBase's box, but there is no reason for it to focus
        // differently from a field. `ring-offset-card` is the local part —
        // it always sits on a card, not on the page background.
        focusRing,
        'focus-visible:ring-offset-card',
        'focus-visible:rounded-sm focus-visible:bg-brand-50/60',
        'data-[empty=true]:before:pointer-events-none data-[empty=true]:before:select-none',
        "data-[empty=true]:before:content-[attr(data-placeholder)]",
        'data-[empty=true]:before:text-muted-foreground/60',
        className,
      )}
    />
  );
}
